import { API, Endpoints, Lang, LangObject } from '@brg/api';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import { LayoutConst, useModal } from 'core';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { CreateLinkTo } from 'shared/constants/linkTo';
import setTextByLang from 'shared/helpers/setTextByLang';

type TeamFormCopyModalProps = {
    form: any;
};

const getDefaultHeader = (form: any): LangObject[] => {
    if (Array.isArray(form?.header) && form.header.length) {
        return form.header.map((item: LangObject) => ({ ...item }));
    }

    return [
        { lang: 'ru', name: '' },
        { lang: 'en', name: '' },
    ];
};

const stripCyrillicFromUnitId = (value: string) =>
    value.replace(/[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/g, '');

const getDefaultName = (form: any, language: string) => {
    if (typeof form?.techName === 'string') {
        return form.techName;
    }

    return Lang(form?.techName, language) || '';
};

const Wrapper = styled(FlexBox)`
    width: 640px;
    max-width: calc(100vw - 32px);
    padding: 32px 40px;
    border-radius: 8px;
    gap: 24px;
    background: ${({ theme }) => theme.mainBg};
    margin: 24px auto;

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        width: calc(100vw - 24px);
        padding: 24px 16px;
    }
`;

const Title = styled.h2`
    margin: 0;
    color: #151514;
    font-size: 28px;
    font-weight: 600;
    line-height: 34px;
    letter-spacing: -0.02em;

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        font-size: 21px;
        line-height: 26px;
    }
`;

export const TeamFormCopyModal: FC<TeamFormCopyModalProps> = ({
    form,
}) => {
    const modal = useModal();
    const history = useHistory();
    const toasts = useToasts();
    const { t, i18n } = useTranslation();
    const [techName, setTechName] = useState(() => getDefaultName(form, i18n.language));
    const [header, setHeader] = useState<LangObject[]>(() => getDefaultHeader(form));
    const [unitId, setUnitId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isSubmitDisabled = useMemo(() => {
        const headerValue = Lang(header, i18n.language);
        return (
            isSubmitting ||
            !unitId.trim() ||
            !form?.formId ||
            !techName.trim() ||
            !headerValue?.trim()
        );
    }, [isSubmitting, unitId, form?.formId, techName, header, i18n.language]);

    const onSubmit = useCallback(() => {
        if (isSubmitDisabled) {
            return;
        }

        setIsSubmitting(true);
        const formId = form.formId;
        const teamId = unitId.trim();

        API.request()
            .endpoint(Endpoints.geograffee)
            .url(`api/forms/${formId}/copy`)
            .post()
            .body({
                teamId,
                header,
                techName: techName.trim(),
            })
            .send()
            .then((response: Response) => {
                if (!response?.ok) {
                    return Promise.reject(response);
                }
                modal.hide();
                history.push(CreateLinkTo.team(teamId));
                toasts.info(t('teams.adminPage.success.form_copy_success'));
            })
            .catch((err: unknown) => {
                setIsSubmitting(false);
                if (typeof Response !== 'undefined' && err instanceof Response && err.status === 403) {
                    toasts.error(t('teams.adminPage.errors.form_copy_unit_access_denied'));
                    return;
                }
                toasts.error(t('teams.adminPage.errors.form_copy_failed'));
            });
    }, [isSubmitDisabled, form?.formId, unitId, header, modal, history, techName, toasts, t]);

    return (
        <Wrapper direction="column" directionMobile="column" align="stretch" alignMobile="stretch">
            <Title>{t('teams.adminPage.modals.form_copy_title')}</Title>
            <FlexBox direction="column" directionMobile="column" gy={24} gyMobile={16} width="100%">
                <CommonInput
                    type={INPUT_TYPES.text}
                    label={t('teams.adminPage.fields.form_copy_unit_id')}
                    value={unitId}
                    onChange={(ev: any) => setUnitId(stripCyrillicFromUnitId(ev.target.value))}
                    orientation="wide"
                    required
                />
                <CommonInput
                    type={INPUT_TYPES.text}
                    label={t('teams.adminPage.fields.form_name')}
                    maxLength={50}
                    value={techName}
                    onChange={(ev: any) => setTechName(ev.target.value)}
                    orientation="wide"
                    required
                />
                <CommonInput
                    type={INPUT_TYPES.text}
                    label={t('teams.adminPage.fields.form_header')}
                    maxLength={200}
                    value={header}
                    useLangSwitcher
                    onChange={(ev: any, lang?: string) => setHeader(setTextByLang(header, lang || i18n.language, ev.target.value))}
                    orientation="wide"
                    required
                />
            </FlexBox>
            <FlexBox width="100%" widthMobile="100%" gx={24} gxMobile={1} gyMobile={8} direction="row" directionMobile="column">
                <BrgButton width="calc(50% - 12px)" fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
                    {t('teams.adminPage.buttons.cancel')}
                </BrgButton>
                <BrgButton width="calc(50% - 12px)" fullWidth variant={ButtonVariant.Filled} disabled={isSubmitDisabled} onClick={onSubmit}>
                    {isSubmitting ? t('teams.adminPage.buttons.copying') : t('teams.adminPage.buttons.copy')}
                </BrgButton>
            </FlexBox>
        </Wrapper>
    );
};
import { API, Endpoints, Lang, LangObject } from '@brg/api';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import { LayoutConst, useModal } from 'core';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { CreateLinkTo } from 'shared/constants/linkTo';
import setTextByLang from 'shared/helpers/setTextByLang';

type TeamFormCopyModalProps = {
    form: any;
};

const getDefaultHeader = (form: any): LangObject[] => {
    if (Array.isArray(form?.header) && form.header.length) {
        return form.header.map((item: LangObject) => ({ ...item }));
    }

    return [
        { lang: 'ru', name: '' },
        { lang: 'en', name: '' },
    ];
};

const stripCyrillicFromUnitId = (value: string) =>
    value.replace(/[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/g, '');

const getDefaultName = (form: any, language: string) => {
    if (typeof form?.techName === 'string') {
        return form.techName;
    }

    return Lang(form?.techName, language) || '';
};

const Wrapper = styled(FlexBox)`
    width: 640px;
    max-width: calc(100vw - 32px);
    padding: 32px 40px;
    border-radius: 8px;
    gap: 24px;
    background: ${({ theme }) => theme.mainBg};
    margin: 24px auto;

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        width: calc(100vw - 24px);
        padding: 24px 16px;
    }
`;

const Title = styled.h2`
    margin: 0;
    color: #151514;
    font-size: 28px;
    font-weight: 600;
    line-height: 34px;
    letter-spacing: -0.02em;

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        font-size: 21px;
        line-height: 26px;
    }
`;

export const TeamFormCopyModal: FC<TeamFormCopyModalProps> = ({
    form,
}) => {
    const modal = useModal();
    const history = useHistory();
    const toasts = useToasts();
    const { t, i18n } = useTranslation();
    const [techName, setTechName] = useState(() => getDefaultName(form, i18n.language));
    const [header, setHeader] = useState<LangObject[]>(() => getDefaultHeader(form));
    const [unitId, setUnitId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isSubmitDisabled = useMemo(() => {
        const headerValue = Lang(header, i18n.language);
        return (
            isSubmitting ||
            !unitId.trim() ||
            !form?.formId ||
            !techName.trim() ||
            !headerValue?.trim()
        );
    }, [isSubmitting, unitId, form?.formId, techName, header, i18n.language]);

    const onSubmit = useCallback(() => {
        if (isSubmitDisabled) {
            return;
        }

        setIsSubmitting(true);
        const formId = form.formId;
        const teamId = unitId.trim();

        API.request()
            .endpoint(Endpoints.geograffee)
            .url(`api/forms/${formId}/copy`)
            .post()
            .body({
                teamId,
                header,
                techName: techName.trim(),
            })
            .send()
            .then((response: Response) => {
                if (!response?.ok) {
                    return Promise.reject(response);
                }
                modal.hide();
                history.push(CreateLinkTo.team(teamId));
                toasts.info(t('teams.adminPage.success.form_copy_success'));
            })
            .catch((err: unknown) => {
                setIsSubmitting(false);
                if (typeof Response !== 'undefined' && err instanceof Response && err.status === 403) {
                    toasts.error(t('teams.adminPage.errors.form_copy_unit_access_denied'));
                    return;
                }
                toasts.error(t('teams.adminPage.errors.form_copy_failed'));
            });
    }, [isSubmitDisabled, form?.formId, unitId, header, modal, history, techName, toasts, t]);

    return (
        <Wrapper direction="column" directionMobile="column" align="stretch" alignMobile="stretch">
            <Title>{t('teams.adminPage.modals.form_copy_title')}</Title>
            <FlexBox direction="column" directionMobile="column" gy={24} gyMobile={16} width="100%">
                <CommonInput
                    type={INPUT_TYPES.text}
                    label={t('teams.adminPage.fields.form_copy_unit_id')}
                    value={unitId}
                    onChange={(ev: any) => setUnitId(stripCyrillicFromUnitId(ev.target.value))}
                    orientation="wide"
                    required
                />
                <CommonInput
                    type={INPUT_TYPES.text}
                    label={t('teams.adminPage.fields.form_name')}
                    maxLength={50}
                    value={techName}
                    onChange={(ev: any) => setTechName(ev.target.value)}
                    orientation="wide"
                    required
                />
                <CommonInput
                    type={INPUT_TYPES.text}
                    label={t('teams.adminPage.fields.form_header')}
                    maxLength={200}
                    value={header}
                    useLangSwitcher
                    onChange={(ev: any, lang?: string) => setHeader(setTextByLang(header, lang || i18n.language, ev.target.value))}
                    orientation="wide"
                    required
                />
            </FlexBox>
            <FlexBox width="100%" widthMobile="100%" gx={24} gxMobile={1} gyMobile={8} direction="row" directionMobile="column">
                <BrgButton width="calc(50% - 12px)" fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
                    {t('teams.adminPage.buttons.cancel')}
                </BrgButton>
                <BrgButton width="calc(50% - 12px)" fullWidth variant={ButtonVariant.Filled} disabled={isSubmitDisabled} onClick={onSubmit}>
                    {isSubmitting ? t('teams.adminPage.buttons.copying') : t('teams.adminPage.buttons.copy')}
                </BrgButton>
            </FlexBox>
        </Wrapper>
    );
};
